/**
 * Helpdesk routes: overview → category → article, each with its own URL so it
 * can be linked, bookmarked and handed over by the assistant.
 *
 * All three read the one cached content payload, so moving between them costs no
 * request. An id that is not in the tree — a stale bookmark, or an article now
 * behind a feature the customer does not have — redirects to the overview rather
 * than erroring: the helpdesk is where you go when you are already lost.
 *
 * `/help/manage` sits outside all of that, and outside the empty state in
 * particular: a helpdesk with nothing published yet is exactly when someone needs
 * to get at the tools for filling it.
 */

import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { MotionConfig } from "motion/react"
import { Navigate, Route, Routes, useParams } from "react-router"
import { useHelpContent } from "./api/helpContentApi"
import type { HelpContent } from "./api/types"
import { HelpdeskArticlePage } from "./components/HelpdeskArticlePage"
import { HelpdeskCategoryPage } from "./components/HelpdeskCategoryPage"
import { HelpdeskEmptyState } from "./components/HelpdeskEmptyState"
import { HelpdeskErrorState } from "./components/HelpdeskErrorState"
import { HelpdeskManage } from "./components/HelpdeskManage"
import { HelpdeskOverview } from "./components/HelpdeskOverview"
import { findArticle, findCategory } from "./content"
import { HELP_BASE } from "./routes"

function CategoryRoute({ content }: { content: HelpContent }) {
  const { categoryId } = useParams()
  const category = categoryId ? findCategory(content, categoryId) : null
  if (!category) return <Navigate to={HELP_BASE} replace />
  return <HelpdeskCategoryPage content={content} category={category} />
}

function ArticleRoute({ content }: { content: HelpContent }) {
  const { articleId } = useParams()
  const location = articleId ? findArticle(content, articleId) : null
  if (!location) return <Navigate to={HELP_BASE} replace />
  return <HelpdeskArticlePage content={content} location={location} />
}

function ManageRoute({ lang }: { lang: string }) {
  const canEdit = useFeatureFlag("HELPDESK_EDIT")
  if (!canEdit) return <Navigate to={HELP_BASE} replace />
  return <HelpdeskManage lang={lang} />
}

function ReaderRoutes({ lang }: { lang: string }) {
  const { data: content, isLoading, isError, refetch } = useHelpContent(lang)

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }
  if (isError || !content) {
    // A failed request is not an empty helpdesk. Keeping these apart is what
    // stops an outage from reading as "there is nothing here for you".
    return <HelpdeskErrorState onRetry={() => void refetch()} />
  }
  if (content.categories.length === 0) {
    return <HelpdeskEmptyState />
  }

  return (
    <Routes>
      <Route index element={<HelpdeskOverview content={content} />} />
      <Route path=":categoryId" element={<CategoryRoute content={content} />} />
      <Route
        path=":categoryId/:articleId"
        element={<ArticleRoute content={content} />}
      />
      <Route path="*" element={<Navigate to={HELP_BASE} replace />} />
    </Routes>
  )
}

export default function HelpdeskApp() {
  const { currentLanguage } = useTranslation()
  const lang = currentLanguage.toLowerCase()

  return (
    // "user": someone who asked their system for less motion gets fades only.
    <MotionConfig reducedMotion="user">
      <div className="h-full min-h-0">
        <Routes>
          <Route path="manage" element={<ManageRoute lang={lang} />} />
          <Route path="*" element={<ReaderRoutes lang={lang} />} />
        </Routes>
      </div>
    </MotionConfig>
  )
}
